import QRCode from "qrcode";
import { downloadFileE2EE, downloadTextE2EE, getDownloadStatus, getUploadPolicy, uploadFileE2EE } from "./e2ee";
import "./styles.css";

/* ── Helpers ────────────────────────────── */

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function parseDownloadKeyFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/download\/([^/]+)$/);
  return match?.[1] ?? null;
}

/* ── Custom Modal ───────────────────────── */

interface ModalButton {
  text: string;
  danger?: boolean;
  onClick: () => void;
}

function showModal(title: string, body: string, buttons: ModalButton[]): void {
  const root = document.querySelector<HTMLDivElement>("#modal-root");
  if (!root) return;

  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";

  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-title">${title}</div>
      <div class="modal-body">${body}</div>
      <div class="modal-actions">
        ${buttons
          .map(
            (b) =>
              `<button class="btn ${b.danger ? "btn-danger" : "btn-secondary"}" data-action="${b.text}">${b.text}</button>`,
          )
          .join("")}
      </div>
    </div>
  `;

  const cleanup = () => overlay.remove();

  buttons.forEach((b) => {
    overlay.querySelector(`[data-action="${b.text}"]`)?.addEventListener("click", () => {
      cleanup();
      b.onClick();
    });
  });

  // Click backdrop to dismiss
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      cleanup();
    }
  });

  root.appendChild(overlay);
}

function showToast(message: string): void {
  const existing = document.querySelector(".toast");
  if (existing) existing.remove();

  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => toast.remove(), 2500);
}

/* ── Upload Icon SVG ────────────────────── */

const UPLOAD_ICON = `
<svg class="upload-zone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
  <polyline points="17 8 12 3 7 8"/>
  <line x1="12" y1="3" x2="12" y2="15"/>
</svg>`;

/* ── Main Render ────────────────────────── */

function renderApp(): void {
  const root = document.querySelector<HTMLDivElement>("#app");
  if (!root) return;

  const downloadKey = parseDownloadKeyFromPath(location.pathname);

  /* ── Download Mode ───────────────────── */
  if (downloadKey) {
    renderDownloadMode(root, downloadKey);
    return;
  }

  /* ── Upload Mode ─────────────────────── */
  renderUploadMode(root);
}

/* ── Download Mode ──────────────────────── */

function renderDownloadMode(root: HTMLDivElement, downloadKey: string): void {
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  const isText = hash.get("type") === "text";

  if (isText) {
    renderTextDownloadMode(root, downloadKey);
    return;
  }

  root.innerHTML = `
    <main class="shell">
      <section class="hero">
        <span class="badge">End-to-End Encrypted</span>
        <h1>neoShare</h1>
        <p>Secure file sharing.</p>
      </section>
      <section class="download-panel">
        <div class="download-meta" id="download-meta">Loading…</div>
        <div class="progress-bar" style="display:none" id="progress-bar">
          <div class="progress-bar-fill" id="progress-fill" style="width:0%"></div>
        </div>
        <div class="status-text" id="status"></div>
        <button class="btn btn-primary" id="download-btn" style="margin-top:16px;display:none">Download & Decrypt</button>
      </section>
    </main>
  `;

  const statusEl = root.querySelector<HTMLElement>("#status")!;
  const downloadMetaEl = root.querySelector<HTMLElement>("#download-meta")!;
  const downloadBtn = root.querySelector<HTMLButtonElement>("#download-btn")!;
  const progressBar = root.querySelector<HTMLElement>("#progress-bar")!;
  const progressFill = root.querySelector<HTMLElement>("#progress-fill")!;

  void getDownloadStatus(downloadKey)
    .then((status) => {
      if (status.exists) {
        downloadMetaEl.innerHTML = `
          <div>Remaining reads: <strong>${status.remainingReads}</strong>${status.lastReadWarning ? ' <span class="danger">(last chance)</span>' : ""}</div>
        `;
        statusEl.textContent = "Ready";
        downloadBtn.style.display = "block";
        return;
      }
      statusEl.textContent = "File not found or has expired.";
      downloadMetaEl.textContent = "";
    })
    .catch(() => {
      statusEl.textContent = "Unable to fetch file status.";
    });

  let pendingDownload = false;

  downloadBtn.addEventListener("click", async () => {
    if (pendingDownload) return;

    let lastWarning = false;
    try {
      const s = await getDownloadStatus(downloadKey);
      lastWarning = s.lastReadWarning;
    } catch { /* proceed anyway */ }

    const startDownload = async () => {
      pendingDownload = true;
      try {
        statusEl.textContent = "Downloading…";
        progressBar.style.display = "block";
        await downloadFileE2EE(downloadKey, {
          onStatus() {},
          onProgress(completed, total) {
            const pct = Math.round((completed / total) * 100);
            progressFill.style.width = `${pct}%`;
            statusEl.textContent = `Decrypting ${pct}%`;
          },
        });
        statusEl.textContent = "Download complete.";
      } catch (error) {
        statusEl.textContent = error instanceof Error ? error.message : "Download failed.";
        progressBar.style.display = "none";
      } finally {
        pendingDownload = false;
      }
    };

    if (lastWarning) {
      showModal(
        "Last Download",
        "This is the final download opportunity for this file. The file will be permanently deleted after this download.",
        [
          { text: "Cancel", onClick: () => {} },
          { text: "Download Anyway", danger: true, onClick: startDownload },
        ],
      );
    } else {
      await startDownload();
    }
  });
}

/* ── Text Download Mode ─────────────────── */

function renderTextDownloadMode(root: HTMLDivElement, downloadKey: string): void {
  root.innerHTML = `
    <main class="shell">
      <section class="hero">
        <span class="badge">End-to-End Encrypted</span>
        <h1>neoShare</h1>
        <p>Secure text sharing.</p>
      </section>
      <section class="download-panel">
        <div class="download-meta" id="download-meta">Loading…</div>
        <div class="progress-bar" style="display:none" id="progress-bar">
          <div class="progress-bar-fill" id="progress-fill" style="width:0%"></div>
        </div>
        <div class="status-text" id="status"></div>
        <button class="btn btn-primary" id="decrypt-btn" style="margin-top:16px;display:none">Decrypt & Display</button>
        <div class="text-display" id="text-display" style="display:none">
          <div class="text-display-header">
            <span class="text-display-label">Decrypted Content</span>
            <button class="btn btn-secondary" id="copy-text-btn" style="width:auto">Copy</button>
          </div>
          <pre class="text-content" id="text-content"></pre>
          <div class="copy-feedback" id="copy-feedback">Copied!</div>
        </div>
      </section>
    </main>
  `;

  const statusEl = root.querySelector<HTMLElement>("#status")!;
  const downloadMetaEl = root.querySelector<HTMLElement>("#download-meta")!;
  const decryptBtn = root.querySelector<HTMLButtonElement>("#decrypt-btn")!;
  const progressBar = root.querySelector<HTMLElement>("#progress-bar")!;
  const progressFill = root.querySelector<HTMLElement>("#progress-fill")!;
  const textDisplay = root.querySelector<HTMLElement>("#text-display")!;
  const textContent = root.querySelector<HTMLElement>("#text-content")!;
  const copyTextBtn = root.querySelector<HTMLButtonElement>("#copy-text-btn")!;
  const copyFeedback = root.querySelector<HTMLElement>("#copy-feedback")!;

  void getDownloadStatus(downloadKey)
    .then((status) => {
      if (status.exists) {
        downloadMetaEl.innerHTML = `
          <div>Remaining reads: <strong>${status.remainingReads}</strong>${status.lastReadWarning ? ' <span class="danger">(last chance)</span>' : ""}</div>
        `;
        statusEl.textContent = "Ready";
        decryptBtn.style.display = "block";
        return;
      }
      statusEl.textContent = "Text not found or has expired.";
      downloadMetaEl.textContent = "";
    })
    .catch(() => {
      statusEl.textContent = "Unable to fetch status.";
    });

  let pendingDecrypt = false;

  decryptBtn.addEventListener("click", async () => {
    if (pendingDecrypt) return;

    let lastWarning = false;
    try {
      const s = await getDownloadStatus(downloadKey);
      lastWarning = s.lastReadWarning;
    } catch { /* proceed anyway */ }

    const startDecrypt = async () => {
      pendingDecrypt = true;
      try {
        statusEl.textContent = "Decrypting…";
        progressBar.style.display = "block";
        decryptBtn.style.display = "none";
        const text = await downloadTextE2EE(downloadKey, {
          onStatus() {},
          onProgress(completed, total) {
            const pct = Math.round((completed / total) * 100);
            progressFill.style.width = `${pct}%`;
            statusEl.textContent = `Decrypting ${pct}%`;
          },
        });
        textContent.textContent = text;
        textDisplay.style.display = "block";
        progressBar.style.display = "none";
        statusEl.textContent = "Decryption complete.";
      } catch (error) {
        statusEl.textContent = error instanceof Error ? error.message : "Decryption failed.";
        progressBar.style.display = "none";
        decryptBtn.style.display = "block";
      } finally {
        pendingDecrypt = false;
      }
    };

    if (lastWarning) {
      showModal(
        "Last Read",
        "This is the final read opportunity. The text will be permanently deleted after this read.",
        [
          { text: "Cancel", onClick: () => {} },
          { text: "Read Anyway", danger: true, onClick: startDecrypt },
        ],
      );
    } else {
      await startDecrypt();
    }
  });

  copyTextBtn.addEventListener("click", async () => {
    const text = textContent.textContent ?? "";
    try {
      await navigator.clipboard.writeText(text);
      copyFeedback.classList.add("visible");
      setTimeout(() => copyFeedback.classList.remove("visible"), 2000);
    } catch {
      // Fallback: select text
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(textContent);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  });
}

/* ── Upload Mode ────────────────────────── */

function renderUploadMode(root: HTMLDivElement): void {
  let selectedFile: File | null = null;
  let shareUrl = "";
  let hasUploaded = false;
  let currentMode: "file" | "text" = "file";

  // Warn on page unload after upload
  window.addEventListener("beforeunload", (e) => {
    if (hasUploaded) {
      e.preventDefault();
      e.returnValue = "";
    }
  });

  root.innerHTML = `
    <main class="shell">
      <section class="hero">
        <span class="badge">End-to-End Encrypted</span>
        <h1>neoShare</h1>
        <p>Secure file sharing.</p>
      </section>

      <div class="mode-toggle" id="mode-toggle">
        <button class="mode-btn active" data-mode="file" id="mode-file-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
          File
        </button>
        <button class="mode-btn" data-mode="text" id="mode-text-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
          Text
        </button>
      </div>

      <div class="upload-zone" id="upload-zone">
        ${UPLOAD_ICON}
        <div class="upload-zone-title" id="zone-title">Drop your file here</div>
        <div class="upload-zone-hint">or click to browse</div>
        <input type="file" id="file-input" />
      </div>

      <div class="text-input-area" id="text-input-area" style="display:none">
        <textarea id="text-content-input" class="text-content-input" placeholder="Enter text to share securely…" rows="8"></textarea>
      </div>

      <div class="file-info" id="file-info" style="display:none">
        <span class="file-name" id="file-name"></span>
        <span class="file-size" id="file-size"></span>
        <button class="remove-file" id="remove-file" title="Remove file">&times;</button>
      </div>

      <div class="options-row">
        <label class="option-label">
          <span>Max Reads</span>
          <input id="max-reads" type="number" min="1" max="1000" value="1" />
        </label>
        <label class="option-label">
          <span>Expiry (hours)</span>
          <input id="expiry-hours" type="number" min="1" value="1" />
        </label>
      </div>

      <div class="policy-hint" id="policy-hint" style="display:none"></div>

      <div class="progress-bar" style="display:none" id="progress-bar">
        <div class="progress-bar-fill" id="progress-fill" style="width:0%"></div>
      </div>
      <div class="status-text" id="status">Ready</div>

      <button class="btn btn-primary" id="upload-btn" disabled>Select a file to upload</button>

      <div class="result" id="result" style="display:none"></div>
    </main>
  `;

  /* ── Element Refs ────────────────────── */
  const uploadZone = root.querySelector<HTMLDivElement>("#upload-zone")!;
  const fileInput = root.querySelector<HTMLInputElement>("#file-input")!;
  const fileInfo = root.querySelector<HTMLDivElement>("#file-info")!;
  const fileNameEl = root.querySelector<HTMLSpanElement>("#file-name")!;
  const fileSizeEl = root.querySelector<HTMLSpanElement>("#file-size")!;
  const removeFileBtn = root.querySelector<HTMLButtonElement>("#remove-file")!;
  const zoneTitle = root.querySelector<HTMLDivElement>("#zone-title")!;
  const maxReadsInput = root.querySelector<HTMLInputElement>("#max-reads")!;
  const expiryHoursInput = root.querySelector<HTMLInputElement>("#expiry-hours")!;
  const uploadBtn = root.querySelector<HTMLButtonElement>("#upload-btn")!;
  const statusEl = root.querySelector<HTMLElement>("#status")!;
  const progressBar = root.querySelector<HTMLElement>("#progress-bar")!;
  const progressFill = root.querySelector<HTMLElement>("#progress-fill")!;
  const resultEl = root.querySelector<HTMLDivElement>("#result")!;
  const modeFileBtn = root.querySelector<HTMLButtonElement>("#mode-file-btn")!;
  const modeTextBtn = root.querySelector<HTMLButtonElement>("#mode-text-btn")!;
  const textInputArea = root.querySelector<HTMLDivElement>("#text-input-area")!;
  const textContentInput = root.querySelector<HTMLTextAreaElement>("#text-content-input")!;

  let policy = { minExpiryHours: 1, maxExpiryHours: 168, maxReadsUpperBound: 1000, defaultExpiryHours: 1 };

  /* ── Mode Toggle ─────────────────────── */
  function switchMode(mode: "file" | "text"): void {
    currentMode = mode;
    if (mode === "file") {
      modeFileBtn.classList.add("active");
      modeTextBtn.classList.remove("active");
      uploadZone.style.display = "block";
      textInputArea.style.display = "none";
      updateUploadButtonState();
    } else {
      modeTextBtn.classList.add("active");
      modeFileBtn.classList.remove("active");
      uploadZone.style.display = "none";
      textInputArea.style.display = "block";
      fileInfo.style.display = "none";
      selectedFile = null;
      uploadBtn.disabled = false;
      uploadBtn.textContent = "Share Text";
    }
  }

  function updateUploadButtonState(): void {
    if (currentMode === "text") {
      uploadBtn.disabled = false;
      uploadBtn.textContent = "Share Text";
    } else if (selectedFile) {
      uploadBtn.disabled = false;
      uploadBtn.textContent = "Upload File";
    } else {
      uploadBtn.disabled = true;
      uploadBtn.textContent = "Select a file to upload";
    }
  }

  modeFileBtn.addEventListener("click", () => switchMode("file"));
  modeTextBtn.addEventListener("click", () => switchMode("text"));

  /* ── Policy Fetch ────────────────────── */
  void getUploadPolicy()
    .then((data) => {
      policy = data;
      expiryHoursInput.min = String(data.minExpiryHours);
      expiryHoursInput.max = String(data.maxExpiryHours);
      expiryHoursInput.value = String(data.minExpiryHours);
      maxReadsInput.max = String(data.maxReadsUpperBound);
      maxReadsInput.value = "1";
    })
    .catch(() => {
      // Use policy defaults already set
      expiryHoursInput.value = String(policy.minExpiryHours);
      maxReadsInput.value = "1";
    });

  /* ── Input Clamping ──────────────────── */
  function clampInputs(): void {
    let v = Number(expiryHoursInput.value);
    if (isNaN(v) || v < policy.minExpiryHours) {
      v = policy.minExpiryHours;
    } else if (v > policy.maxExpiryHours) {
      v = policy.maxExpiryHours;
    }
    expiryHoursInput.value = String(v);

    let r = Number(maxReadsInput.value);
    if (isNaN(r) || r < 1) {
      r = 1;
    } else if (r > policy.maxReadsUpperBound) {
      r = policy.maxReadsUpperBound;
    }
    maxReadsInput.value = String(r);
  }

  expiryHoursInput.addEventListener("blur", clampInputs);
  expiryHoursInput.addEventListener("change", clampInputs);
  maxReadsInput.addEventListener("blur", clampInputs);
  maxReadsInput.addEventListener("change", clampInputs);

  /* ── File Selection ──────────────────── */
  function selectFile(file: File): void {
    if (selectedFile && selectedFile !== file) {
      // Confirm before replacing
      showModal(
        "Replace file?",
        "You already have a file selected. Selecting a new file will replace it.",
        [
          { text: "Cancel", onClick: () => {} },
          { text: "Replace", danger: true, onClick: () => setFile(file) },
        ],
      );
      // Reset the input so the same file can be re-selected
      fileInput.value = "";
      return;
    }
    setFile(file);
  }

  function setFile(file: File): void {
    selectedFile = file;
    fileNameEl.textContent = file.name;
    fileSizeEl.textContent = formatBytes(file.size);
    fileInfo.style.display = "flex";
    zoneTitle.textContent = "File selected";
    uploadZone.classList.add("has-file");
    updateUploadButtonState();
  }

  function clearFile(): void {
    selectedFile = null;
    fileInfo.style.display = "none";
    zoneTitle.textContent = "Drop your file here";
    uploadZone.classList.remove("has-file");
    uploadBtn.disabled = true;
    uploadBtn.textContent = "Select a file to upload";
    fileInput.value = "";
  }

  /* ── Drag & Drop ─────────────────────── */
  uploadZone.addEventListener("click", () => fileInput.click());

  uploadZone.addEventListener("dragover", (e) => {
    e.preventDefault();
    uploadZone.classList.add("dragover");
  });

  uploadZone.addEventListener("dragleave", () => {
    uploadZone.classList.remove("dragover");
  });

  uploadZone.addEventListener("drop", (e) => {
    e.preventDefault();
    uploadZone.classList.remove("dragover");
    const file = e.dataTransfer?.files?.[0];
    if (file) selectFile(file);
  });

  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) selectFile(file);
  });

  removeFileBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    clearFile();
  });

  /* ── Upload ──────────────────────────── */
  uploadBtn.addEventListener("click", async () => {
    if (currentMode === "text") {
      const textContent = textContentInput.value.trim();
      if (!textContent) {
        showToast("Please enter some text to share.");
        return;
      }
      if (textContent.length > 500 * 1024) {
        showToast("Text is too long. Please keep it under 500 KB.");
        return;
      }
    } else if (!selectedFile) {
      return;
    }

    // If user already uploaded something, warn before replacing
    if (hasUploaded) {
      showModal(
        "Upload new?",
        "You already have an active share link. Creating a new one will permanently invalidate the previous link, and it cannot be recovered.",
        [
          { text: "Cancel", onClick: () => {} },
          { text: "Replace", danger: true, onClick: () => doUpload() },
        ],
      );
      return;
    }

    await doUpload();
  });

  async function doUpload(): Promise<void> {
    let file: File;
    if (currentMode === "text") {
      const textContent = textContentInput.value.trim();
      file = new File([textContent], "shared-text.txt", { type: "text/plain" });
    } else {
      file = selectedFile!;
    }

    // Clamp one final time for safety
    clampInputs();
    const expiresInHours = Number(expiryHoursInput.value);
    const maxReads = Number(maxReadsInput.value);

    try {
      uploadBtn.disabled = true;
      uploadBtn.textContent = "Uploading…";
      progressBar.style.display = "block";
      progressFill.style.width = "0%";
      statusEl.textContent = "Initializing…";

      const result = await uploadFileE2EE(file, {
        maxReads,
        expiresInHours,
        contentType: currentMode,
        onProgress(completed, total) {
          const pct = Math.round((completed / total) * 100);
          progressFill.style.width = `${pct}%`;
          statusEl.textContent = `Uploading ${pct}%`;
        },
      });

      shareUrl = result.shareUrl;
      hasUploaded = true;
      statusEl.textContent = "Upload complete.";
      progressBar.style.display = "none";
      uploadBtn.style.display = "none";
      uploadZone.style.display = "none";
      fileInfo.style.display = "none";
      textInputArea.style.display = "none";

      showResult(resultEl, shareUrl);
      showToast("Share link ready — do not refresh the page.");
    } catch (error) {
      statusEl.textContent = error instanceof Error ? error.message : "Upload failed.";
      progressBar.style.display = "none";
      uploadBtn.disabled = false;
      uploadBtn.textContent = currentMode === "text" ? "Share Text" : "Upload File";
    }
  }

  /* ── Result Panel (QR + Copy) ────────── */
  async function showResult(container: HTMLElement, url: string): Promise<void> {
    container.style.display = "block";

    // Generate QR code as data URL
    let qrDataUrl = "";
    try {
      qrDataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      });
    } catch {
      // QR generation failed — non-critical
    }

    container.innerHTML = `
      <div class="result-title">Share Link</div>
      ${qrDataUrl ? `<div class="qr-wrapper"><img src="${qrDataUrl}" alt="QR Code" width="200" height="200" /></div>` : ""}
      <div style="display:flex;gap:8px;margin-top:12px">
        <input type="text" readonly value="${url}" style="flex:1;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:10px 12px;color:var(--text);font-size:0.8rem;font-family:monospace;text-overflow:ellipsis;" id="share-url-input" />
        <button class="btn btn-secondary" id="copy-btn" style="width:auto;flex-shrink:0;">Copy</button>
      </div>
      <div class="copy-feedback" id="copy-feedback">Copied!</div>
      <div class="result-actions">
        <button class="btn btn-secondary" id="new-upload-btn">Upload Another</button>
      </div>
    `;

    container.querySelector("#copy-btn")?.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(url);
        const fb = container.querySelector<HTMLElement>("#copy-feedback");
        if (fb) {
          fb.classList.add("visible");
          setTimeout(() => fb.classList.remove("visible"), 2000);
        }
      } catch {
        // Fallback: select the input text
        const input = container.querySelector<HTMLInputElement>("#share-url-input");
        if (input) {
          input.select();
          input.setSelectionRange(0, 99999);
        }
      }
    });

    container.querySelector("#new-upload-btn")?.addEventListener("click", () => {
      showModal(
        "Create new share?",
        "This will permanently destroy the current share link. The recipient will no longer be able to access the content. Are you sure?",
        [
          { text: "Cancel", onClick: () => {} },
          {
            text: "Continue",
            danger: true,
            onClick: () => {
              hasUploaded = false;
              shareUrl = "";
              resultEl.style.display = "none";
              uploadBtn.style.display = "block";
              statusEl.textContent = "Ready";
              if (currentMode === "text") {
                textInputArea.style.display = "block";
                textContentInput.value = "";
              } else {
                uploadZone.style.display = "block";
                clearFile();
              }
            },
          },
        ],
      );
    });
  }
}

renderApp();
